import Image from 'next/image';
import {useCallback, useState} from 'react';
import {useSession} from 'next-auth/react';
import {useMediaForOneSku} from '@components/product/global/tabs/MediaPhotos/MediaHelpers';
import {Center} from '@components/checkout-v2/desktop/checkout-helper';
import {Spinner} from '@components/widgets/Common';
import {UploadMediaForProductTab} from '@components/product/global/UserMediaUpload';
import {AdminPanelUserSubmittedMedia} from '@components/product/global/tabs/MediaImagesAdminPanel';
import {ModalGalleryV3_YARLB} from '@components/product/desktop/ModalGalleryYetAnotherReactLightBox';

export const ProductPageTabCustomerPhotosAndMedia = ({sku, skuList}) => {
  const {data: session} = useSession();
  const isAdminVisitor =
    session?.user?.isAdmin || session?.user?.email === 'kevinschlenker01@gmail.com';
  // console.log("[ProductPageTabCustomerPhotosAndMedia] isAdminVisitor:", isAdminVisitor);

  const [currentImage, setCurrentImage] = useState(0);
  const [viewerIsOpen, setViewerIsOpen] = useState(false);
  const [showUpload, setShowUpload] = useState(false);

  const {images, mutateMedia, isLoading, isLoadedOnce} = useMediaForOneSku(skuList, {
    status: 'approved'
  });
  // console.log('[ProductPageTabCustomerPhotosAndMedia] images:', images);

  const openLightbox = useCallback((index) => {
    setCurrentImage(index);
    setViewerIsOpen(true);
  }, []);

  const closeLightbox = () => {
    setCurrentImage(0);
    setViewerIsOpen(false);
  };

  const showLoading = isLoading && !isLoadedOnce;

  const imagesWithDetails = images?.map((image, index) => {
    let imageUrl = image?.image_url ?? image?.url ?? image?.src;

    let caption = image?.caption ?? null;

    let altText;
    if (sku && !altText) {
      altText = `Image of ${sku} ${index + 1}`;
    } else {
      altText = `Image ${index + 1}`;
    }

    return {
      url: imageUrl,
      width: image?.width ?? 1600,
      height: image?.height ?? 900,
      altText: altText,
      caption: caption
    };
  });
  // console.log('[ProductPageTabCustomerPhotosAndMedia] imagesWithDetails:', imagesWithDetails);

  return (
    <div className="overflow-y-auto overflow-x-hidden" style={{maxHeight: 800}}>
      {isAdminVisitor && (
        <AdminPanelUserSubmittedMedia sku={sku} skuList={skuList} openLightbox={openLightbox} />
      )}

      {!isAdminVisitor && (
        <>
          <div className={'mt-2 px-2'}>
            <UploadMediaForProductTab
              sku={sku}
              mutateMedia={mutateMedia}
              showUpload={showUpload}
              setShowUpload={setShowUpload}
              title={'Upload your image'}
              subtitle={'Upload your images here'}
            />
            <div className="my-4 border-t border-gray-200"></div>
          </div>

          {showLoading && (
            <Center className="p-5 m-5">
              <Spinner animation="border" />
            </Center>
          )}

          {!showUpload && (
            <div className="flex flex-wrap">
              {imagesWithDetails?.map((image, index) => {
                // console.log("[ProductPageTabCustomerPhotosAndMedia][image]", image)
                const keyId = index + 1;
                const imgUrl = image?.image_url ?? image?.url ?? image?.src;
                const width = image?.width;
                const height = image?.height;
                const altText = image?.altText;
                // console.log('[ProductPageTabCustomerPhotosAndMedia][imgUrl]', imgUrl);
                return (
                  <div key={keyId} className="w-1/3 aspect-square gap-2 px-1">
                    <GalleryImage
                      index={index}
                      src={imgUrl}
                      width={width}
                      height={height}
                      altText={altText}
                      onClick={openLightbox}
                      images={imagesWithDetails}
                    />
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}

      <ModalGalleryV3_YARLB
        sku={sku}
        images={imagesWithDetails}
        viewerIsOpen={viewerIsOpen}
        photoIndex={currentImage}
        setPhotoIndex={setCurrentImage}
        closeLightbox={closeLightbox}
      />
    </div>
  );
};

const GalleryImage = ({index, onClick, src, width, height, images, altText}) => {
  return (
    <div
      className={`relative cursor-pointer m-1 bg-black`}
      onClick={(e) => onClick(index, images)}
      style={{
        width: '100%',
        height: '100%',
        overflow: 'hidden',
        borderRadius: 5
      }}>
      <Image
        width={width ?? 200}
        height={height ?? 200}
        src={src}
        alt={altText ?? `Customer Submitted Image #${index + 1}`}
        className="aspect-square rounded w-full h-full object-cover object-center"
        sizes={'200px'}
      />
    </div>
  );
};
